import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingInfo, ProductSearchResultDto } from '@scip/api';
import { combineLatest, Observable, of } from 'rxjs';
import { delay, map, mapTo, startWith, take, tap } from 'rxjs/operators';
import { ProductTab, translationKeys } from '../../core';

import { NotificationService, ProductsService, ProductTabIdentifier, ProductTableService, ProductTabsService, SearchService } from '../../services';
import { AvanoLoadingIndicatorService } from '@avano/avano-components';
import { TabId } from '@avano/avano-components/avano-tabs/tab';
import { ProductsPageActions, selectProductsPageTableState } from '../../store/products-page';
import { Store } from '@ngrx/store';

@Component({
  selector: 'scip-products-page',
  templateUrl: './products-page.component.html',
  styleUrls: ['./products-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductsPageComponent implements OnInit, OnDestroy {
  currentTabIndex: TabId = 'FIXED';
  productTabs$: Observable<ProductTab[]>;
  productTabs: ProductTab[] = [];
  tabsLoaded$: Observable<boolean>;
  translationKeys: typeof translationKeys = translationKeys;
  loadingInfo$: Observable<LoadingInfo> = this.store.select(selectProductsPageTableState);
  private productChanged = false;

  @HostListener('window:beforeunload', ['$event'])
  private attachToBeforeUnloadEvent(originalEvent: any): void {
    if (this.productsService.getChangedTabIds().length) {
      originalEvent.preventDefault();
      // This does not need to be i18n, because the browsers do
      // not allow anymore to set the text of the on-unload-confirmdialog
      const confirmationMessage = 'Unsaved changes';
      originalEvent.returnValue = confirmationMessage;
    }
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  constructor(
    private productTableService: ProductTableService,
    private productsService: ProductsService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private searchService: SearchService,
    private router: Router,
    private productTabsService: ProductTabsService,
    private changeDet: ChangeDetectorRef,
    private avanoLoadingIndicatorService: AvanoLoadingIndicatorService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.productTabs$ = combineLatest([this.productTabsService.tabs, this.productsService.getUserEditedKeysAsArray().pipe(startWith([]))]).pipe(
      tap(([productTabs, changedTabs]: [ProductTab[], ProductTabIdentifier[]]) => {
        this.updateTabs(productTabs, changedTabs);
      }),
      mapTo(this.productTabs)
    );

    this.tabsLoaded$ = this.productTabsService.tabs.pipe(
      map((tabs: ProductTab[]) => tabs.every(({ name }: ProductTab) => !!name)),
      tap((loaded) => {
        if (loaded && this.productTabs.length) {
          this.currentTabIndex = this.productTabs[this.productTabs.length - 1].id as TabId;
          this.changeDet.markForCheck();
        }
      })
    );
  }

  onCloseTriggered(id: TabId): void {
    this.cancelProductTab(`${id}`);
  }

  // Triggered by clicking cancel on the buttonbar within a product tab
  cancelProductTab(id: string): void {
    const ids = this.route.snapshot.queryParamMap.getAll('productId').filter((productId) => productId !== id);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        productId: ids
      },
      replaceUrl: true
    });

    this.checkForRefreshingTable();
  }

  openProductDetails(products: ProductSearchResultDto[]): void {
    const existingIds = this.route.snapshot.queryParamMap.getAll('productId').map((id) => +id);
    const newIds = products
      // Remove products that are not completely loaded into scip from list of product to open
      .filter(({ lockedForUpdate, name }: ProductSearchResultDto) => {
        if (lockedForUpdate) {
          this.notificationService.showInfo(translationKeys.productsComponent.typescript.msg.productNotLoadedInfo, { productName: name });
          return false;
        }
        return true;
      })
      .map(({ id }: ProductSearchResultDto) => id);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        productId: [...new Set([...existingIds, ...newIds])]
      },
      replaceUrl: true,
      queryParamsHandling: 'merge'
    });
  }

  handleDossierUploadInTab(): void {
    this.productChanged = true;
  }

  // Triggered by clicking any tab
  onChange(event: { index: number }): void {
    // Only if the products-table tab is opened we need to check and refresh
    if (event.index === 0) {
      this.checkForRefreshingTable();
    }
  }

  saveProductTab(productIdentifier: ProductTabIdentifier): void {
    this.avanoLoadingIndicatorService.blockApplication();
    this.productsService
      .saveTab(productIdentifier)
      .pipe(take(1))
      .subscribe((saved: boolean) => {
        // If in that tab a save was successfully then set the boolean for the list-reloading
        this.productChanged = this.productChanged || saved;
        this.avanoLoadingIndicatorService.unblockApplication();
      });
  }

  ngOnDestroy(): void {
    this.productTabsService.removeAllTabs();
    this.productsService.discardAllTabs();
    this.searchService.cleanSearchCaches();
    this.store.dispatch(ProductsPageActions.clear());
  }

  private checkForRefreshingTable(): void {
    // Only if there was a dossier upload from the current user initiated
    // we will automatically refresh the table on showing
    if (this.productChanged) {
      this.productTableService.triggerRefreshProductsTable();
      this.productChanged = false; // Reset it because we refreshed the table
    }
  }

  private updateTabs(productTabs: ProductTab[], changedTabs: ProductTabIdentifier[]): void {
    if (productTabs.length > this.productTabs.length) {
      const newTabs: ProductTab[] = productTabs.filter((tab) => !this.productTabs.find(({ id }) => id === tab.id));
      this.productTabs.push(...newTabs);
      this.setActiveTab();
    } else if (productTabs.length < this.productTabs.length) {
      this.productTabs.forEach((tab, i) => {
        if (!productTabs.find(({ id }) => id === tab.id)) {
          this.productTabs.splice(i, 1);
        }
      });
      const lastIndex = this.productTabs.length - 1;
      this.currentTabIndex = lastIndex !== -1 ? this.productTabs[lastIndex].id : 'FIXED';
      this.changeDet.markForCheck();
    }
    this.setProductNames(productTabs);
    this.setChangedTabs(changedTabs);
  }

  private setProductNames(productTabs: ProductTab[]): void {
    this.productTabs.forEach((productTab) => {
      if (productTab.name) {
        return;
      } else {
        const index = productTabs.findIndex(({ id }) => id === productTab.id);
        productTab.name = productTabs[index].name;
      }
    });
  }

  private setActiveTab(): void {
    const lastIndex = this.productTabs.length - 1;
    this.currentTabIndex = lastIndex !== -1 ? this.productTabs[lastIndex].id : 'FIXED';
    this.changeDet.markForCheck();
  }

  private setChangedTabs(changedTabs: ProductTabIdentifier[]): void {
    this.productTabs.forEach((tab: ProductTab, i: number) => {
      this.productTabs[i].isUnsaved = changedTabs.some((id) => id === tab.id);
    });
  }
}
